import { Images } from '../../../../@resources/img';
import { Arr } from '../../../../CodesForFoundation';
import { uuid } from '../../../../CodesThatsGottaGoSomewhereElse';
import { IsolatedObjectManager } from '../../../../concepts/objects/IsolatedObjectManager';
import { Physical } from '../../../canvas/entities/Physical';
import { GridLayout } from '../../../canvas/layout/GridLayout';
import { Pen } from '../../../canvas/layout/Pen';
import { PhysicalPlot } from '../../../canvas/layout/Plot';
import { IsActuallyConcealed } from '../../conceal/Concealable';
import { Sticky } from '../../paper/Sticky';
import { Topic } from '../../topics/Topic';
import { TopicAssocation, TopicBoundaryAttr } from '../../topics/TopicAssociations';
import { TabularImportGroupingStep } from '../steps/TabularImportGroupingStep';
import { TabularImportMappingStep } from '../steps/TabularImportMappingStep';
import { TabularImportStepPreviewGenerator } from '../steps/TabularImportStepPreview';
import {
    MappingFilter,
    MappingTarget,
    TabularData,
    TabularDataDimension,
    TabularImportParams,
    TabularImportStrategy
} from '../TabularImportModel';
import { ContrastColorList } from './ContrastColorList';

const selectColorForKey = (allKeys: string[], inputKey: string) => {
    // const rand = alea(key, { global: true });
    // const colors = ColorList.slice(0, -4);
    // return colors[Math.floor(rand() * colors.length)];
    return Arr.at(ContrastColorList, allKeys.indexOf(inputKey));
};

const previewStickiesInTopics: TabularImportStepPreviewGenerator = (data, params, page) => {

    const grouping = params.grouping as TabularDataDimension;
    if (!grouping) return { figures: [], pages: 0 };

    const items = params.hasHeaders ? data.slice(1) : data;
    const grouped = Arr.groupBy(items, grouping.key);
    const keys = Object.keys(grouped);
    const counts = Object.fromEntries(Object.entries(grouped).map(x => [x[0], x[1].length]));

    const selected = Arr.at(keys, page);

    const iom = new IsolatedObjectManager();
    const figures = [] as Physical[];

    const topic = iom.create(Topic);
    topic.label = selected;
    topic.color = selectColorForKey(keys, selected);
    const ps = topic.getPreferredSize();
    topic.width = ps.w;
    topic.height = ps.h;
    topic.moveBy(topic.width / -2, (topic.height / -2));
    figures.push(topic);

    {
        const sticky = iom.create(Sticky);
        sticky.moveBy(sticky.width / -2, (sticky.height / -2));
        sticky.moveBy(0, 150);
        sticky.content = 'Nobody can see, so who cares what this says.';
        IsActuallyConcealed.set(sticky, true);
        TopicAssocation.set(sticky, topic.id);
        figures.push(sticky);
    }

    {
        const sticky = iom.create(Sticky);
        sticky.moveBy(sticky.width / -2, (sticky.height / -2));
        sticky.moveBy(140, 160);
        sticky.content = 'Who cares what this says, nobody can see.';
        IsActuallyConcealed.set(sticky, true);
        TopicAssocation.set(sticky, topic.id);
        figures.push(sticky);
    }

    {
        const sticky = iom.create(Sticky);
        sticky.moveBy(sticky.width / -2, (sticky.height / -2));
        sticky.moveBy(-120, 140);
        sticky.content = `${counts[selected]} stickies in this group.`;
        // zone.add(sticky);
        TopicAssocation.set(sticky, topic.id);
        figures.push(sticky);
    }

    TopicBoundaryAttr.set(topic, Topic.computeBoundary(topic, figures.slice(1)));

    return { figures, pages: keys.length };

};

const composeStickiesInTopics = (data: TabularData, params: TabularImportParams) => {
    const { mappings, grouping } = params;

    const iom = new IsolatedObjectManager();
    const items = params.hasHeaders ? data.slice(1) : data;
    const grouped = Arr.groupBy(items, grouping.key);
    const keys = Object.keys(grouped);

    // Produce the stickies (indexed by the source item)
    const stickies = new Map(items.map(tm => {
        const sk = iom.create(Sticky);
        for (const [t, s] of mappings) {
            sk.set(t.path, t.filter(tm[s.key]));
        }
        return [tm, sk];
    }));

    // Produce the topics (indexed by their key)
    const topics = new Map(keys.map(ky => {
        const tp = iom.create(Topic);
        tp.label = ky;
        tp.color = selectColorForKey(keys, ky);
        const ps = tp.getPreferredSize();
        tp.width = ps.w;
        tp.height = ps.h;
        return [ky, tp];
    }));

    // Match up the stickies and topics, compute sub-plots and bundle together
    const bundles = Object.entries(grouped).map(([ky, tms]) => {
        const tp = topics.get(ky);
        const sks = tms.map(x => stickies.get(x));
        sks.forEach(s => TopicAssocation.set(s, tp.id));
        const pts = GridLayout.compute(Pen.from(sks));
        pts.add(PhysicalPlot.for(tp).moveTo(pts.bounds().center().x - (tp.width / 2), 0 - tp.height - 20));
        pts.apply();
        return [tp, sks, pts] as const;
    });

    // Combine all figures into single array and all pens into another, arrange the pens and apply to the figures
    const figures = bundles.flatMap(x => [x[0], ...x[1]]);
    const pens = Pen.wrap(bundles.flatMap(x => x[2]));
    GridLayout.compute(pens, 60).apply();

    return figures.map(x => x.toTuple());
};

export const StickiesInTopicsTabularImportStrategy: TabularImportStrategy = {
    id: uuid(),
    label: 'Import as Stickies In Topics',
    image: Images.ImportStickiesInTopics,
    steps: [
        TabularImportMappingStep({
            type: Sticky,
            typeLabel: 'Stickies',
            preview: [2, 4],
            targets: [
                MappingTarget('Content', 'content', MappingFilter.text(140)),
                MappingTarget('Color', 'background', MappingFilter.color),
            ]
        }),
        TabularImportGroupingStep({
            label: 'Stickies',
            preview: previewStickiesInTopics,
        }),
    ],
    apply: composeStickiesInTopics,
};
